/**
 * RAM — Change Statement Preferences (Wave 10 fallback replacement).
 *
 * POST /v3/maint/acct/change-statement-prefs
 *
 * Replaces the Wave10ActionFallback which hardcoded
 * { eStatement: true, paperStatement: false } regardless of FSR intent.
 * Surfaces the e-statement / paper toggles, frequency, and reason so the
 * jXchange ElecStmtType wire reflects the actual customer choice and
 * closes the NEWESI weekly-exception loop.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import { emitDisclosureRedeliveryRequired } from '../disclosureTriggers';
import type { RamTarget } from '../types';

export interface ChangeStatementPrefsProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

export default function ChangeStatementPrefs({
  target,
  onDone,
  onCancel,
}: ChangeStatementPrefsProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const { submit, submitting, error } = useMaintActionSubmit();
  const [eStatement, setEStatement] = useState<boolean>(true);
  const [paperStatement, setPaperStatement] = useState<boolean>(false);
  const [frequency, setFrequency] = useState<string>('monthly');
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  const atLeastOneChannel = eStatement || paperStatement;
  const canSave = !submitting && atLeastOneChannel && Boolean(target.acctId);

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId) return;
    const result = await submit(
      (b) => client.retail.maintActions.submitChangeStatementPrefs(b),
      {
        accountNumber: target.acctId,
        eStatement,
        paperStatement,
        frequency: frequency || undefined,
        reason: reason.trim() || undefined,
      },
    );
    if (!result.success) return;
    // Reg E §1005.10 — redeliver ESIGN consent ONLY when activating
    // e-statements (predicate). Paper-only or no-change does not trigger.
    if (eStatement) {
      void emitDisclosureRedeliveryRequired(client, {
        action: 'change-statement-prefs',
        cifId: target.cifId,
        accountNumber: target.acctId,
        actorUpn: user?.upn,
        payload: { eStatement, paperStatement, frequency },
      });
    }
    if (result.data?.approvalRequired) {
      setApprovalNote(
        `Submitted — awaiting ${result.data.approverRole ?? 'approver'} approval (scenario: ${result.data.approvalScenario ?? 'default'}).`,
      );
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped. Audit row recorded.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          Statement preferences updated for account {target.acctId}.
        </Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Change statement preferences</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
        {target.acctId && <Pill variant="muted">Account {target.acctId}</Pill>}
      </div>

      <Banner tone="info">
        Switching to e-statements requires the customer to have completed the
        E-Sign Consent. Closes the NEWESI weekly-exception loop.
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          <input
            type="checkbox"
            checked={eStatement}
            onChange={(e) => setEStatement(e.target.checked)}
          />
          {' '}E-statement
        </label>
        <label className="ram-search__label">
          <input
            type="checkbox"
            checked={paperStatement}
            onChange={(e) => setPaperStatement(e.target.checked)}
          />
          {' '}Paper statement
        </label>
        <label className="ram-search__label">
          Frequency
          <select
            className="ram-input"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
            <option value="on-demand">On-demand</option>
          </select>
        </label>
        <label className="ram-search__label">
          Reason
          <textarea
            className="ram-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={512}
          />
        </label>
      </div>

      {!atLeastOneChannel && (
        <Banner tone="warn">
          Select at least one delivery channel (e-statement or paper).
        </Banner>
      )}

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Submitting…' : 'Submit statement prefs change'}
        </Button>
      </div>
    </div>
  );
}
